'use client';
import { MultiServicePreset } from '@/types';

import React, { useEffect, useRef, useState } from 'react';
import { useConfig } from '@/app/contexts/ConfigContext';
import CustomLink from './CustomLink';
import { ServiceType } from '@/types';
import { SERVICE_TYPES } from '@/helpers/enums';

const MenuBurger: React.FC = () => {
  const { config, wording, setConfig, updateWording, selectedLanguage } =
    useConfig();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Use to close the menu if user click out of it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isDropdownOpen) return;

    const closeMenu = () => setIsDropdownOpen(false);

    // Capture scroll events from the page and from any nested scrollable area.
    document.addEventListener('scroll', closeMenu, true);
    return () => document.removeEventListener('scroll', closeMenu, true);
  }, [isDropdownOpen]);

  const handleConfig = (type: ServiceType) => {
    setConfig({
      ...config,
      serviceType: type,
    });
    updateWording(type, selectedLanguage);
  };
  type MenuEntry = {
    href: string;
    label: string | undefined;
    serviceType?: ServiceType;
  };

  const multiFitnessYogaPilatesMediaEntries: MenuEntry[] = [
    {
      href: '/media/media_1',
      label: wording?.components_header_media_1,
      serviceType: SERVICE_TYPES.FITNESS,
    },
    {
      href: '/media/media_2',
      label: wording?.components_header_media_2,
      serviceType: SERVICE_TYPES.FITNESS,
    },
    {
      href: '/media/media_all',
      label: wording?.components_header_media_multi_content_2,
      serviceType: SERVICE_TYPES.YOGA,
    },
    {
      href: '/media/media_all',
      label: wording?.components_header_media_multi_content_3,
      serviceType: SERVICE_TYPES.PILATES,
    },
  ];

  const multiAstrologyMeditationLoveMediaEntries: MenuEntry[] = [
    {
      href: '/media/media_all',
      label: wording?.components_header_media_content,
      serviceType: SERVICE_TYPES.ASTROLOGY,
    },
    {
      href: '/media/media_all',
      label: wording?.components_header_media_multi_content_2,
      serviceType: SERVICE_TYPES.MEDITATION,
    },
    {
      href: '/media/media_all',
      label: wording?.components_header_media_multi_content_3,
      serviceType: SERVICE_TYPES.LOVE,
    },
  ];

  const standardMediaEntries: MenuEntry[] =
    config.serviceType === SERVICE_TYPES.FITNESS
      ? multiFitnessYogaPilatesMediaEntries.slice(0, 2)
      : [
          {
            href: '/media/media_all',
            label: wording?.components_header_media_content,
          },
        ];

  let mediaEntries: MenuEntry[] = standardMediaEntries;
  if (config.multiService === MultiServicePreset.FYP) {
    mediaEntries = multiFitnessYogaPilatesMediaEntries;
  } else if (config.multiService === MultiServicePreset.AML) {
    mediaEntries = multiAstrologyMeditationLoveMediaEntries;
  }

  const textEntries: MenuEntry[] = [
    {
      href: '/text1',
      label: wording?.components_header_text1,
      serviceType: config.multiService === MultiServicePreset.AML
        ? SERVICE_TYPES.MEDITATION
        : undefined,
    },
    {
      href: '/text2',
      label: wording?.components_header_text2,
      serviceType: config.multiService === MultiServicePreset.AML
        ? SERVICE_TYPES.ASTROLOGY
        : undefined,
    },
  ];

  const menuEntries = [...mediaEntries, ...textEntries];

  return (
    <div
      className='2xl:hidden absolute right-2 top-2 text-[40px]'
      ref={dropdownRef}
    >
      <div
        className={`${isDropdownOpen ? 'rotate-90' : ''}`}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        style={{
          color: config.theme.textColorDark,
          backgroundColor: config.theme.backgroundColor,
        }}
      >
        ☰
      </div>

      {isDropdownOpen && (
        <ul
          className='fixed inset-x-0 top-20 2xl:top-[120px] border shadow-lg-both h-[60vh] flex flex-col justify-evenly items-center font-bold'
          style={{
            zIndex: 10000,
            borderColor: config.theme.backgroundColor,
            backgroundColor: config.theme.backgroundColor,
          }}
        >
          {menuEntries.map((entry) => (
            <li
              key={`${entry.href}-${entry.label ?? 'entry'}`}
              className='px-3 py-2 cursor-pointer flex items-center gap-2 h-8 whitespace-nowrap min-w-[105px] max-w-[105px] flex justify-center'
              style={{
                color: config.theme.textColorDark,
                backgroundColor: config.theme.backgroundColor,
              }}
            >
              <CustomLink
                className='text-sm leading-none text-xl'
                href={entry.href}
                onClick={() => {
                  const serviceType = entry.serviceType;
                  if (serviceType) {
                    handleConfig(serviceType);
                  }
                  setIsDropdownOpen(false);
                }}
              >
                {entry.label} &gt;
              </CustomLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MenuBurger;
